import { getFalModelById, listFalModels, resolveFalCanonicalModelId } from './fal';
import { listOfficialReplicateModels, listOfficialReplicateModelsByKind } from './replicate';
const isFalSeedreamModel = (model) => model.id.startsWith('fal-ai/bytedance/seedream/');
const isFalGptImageModel = (model) => model.id === 'openai/gpt-image-2';
const resolveReplicateRateLimitKey = (model) => {
    if (model.model === 'openai/sora-2') {
        return 'openai:sora-2';
    }
    if (model.model === 'openai/sora-2-pro') {
        return 'openai:sora-2-pro';
    }
    if (model.owner === 'openai') {
        return 'openai:default';
    }
    return 'replicate:default';
};
const shouldEnableBatchGenerations = (model) => {
    if (model.kind !== 'image') {
        return false;
    }
    if (model.id === 'google/nano-banana' ||
        model.id === 'fal-ai/nano-banana' ||
        model.id === 'fal-ai/nano-banana-pro' ||
        model.id === 'fal-ai/nano-banana-2') {
        return true;
    }
    if (model.id.startsWith('bytedance/seedream') || model.id.startsWith('fal-ai/bytedance/seedream')) {
        return true;
    }
    if (model.owner === 'black-forest-labs' && model.model.includes('flux')) {
        return true;
    }
    if (model.id.startsWith('fal-ai/flux-pro/')) {
        return true;
    }
    return false;
};
const applyBatchGenerationOverrides = (model) => {
    if (!shouldEnableBatchGenerations(model)) {
        return model;
    }
    const settings = model.settings ?? {};
    const properties = {
        ...settings.properties
    };
    const existingNumOutputs = properties.num_outputs;
    const numOutputsSchema = {
        title: 'Concurrent shots',
        description: 'Generate multiple images in parallel (uses one credit bundle per shot).',
        type: 'integer',
        default: 1,
        minimum: 1,
        maximum: 4,
        multipleOf: 1,
        order: 1
    };
    properties.num_outputs = {
        ...existingNumOutputs,
        ...numOutputsSchema
    };
    const hiddenSettings = model.ui?.hiddenSettings?.filter((key) => key !== 'num_outputs');
    const labelOverrides = {
        ...(model.ui?.labelOverrides ?? {}),
        num_outputs: 'Concurrent shots'
    };
    return {
        ...model,
        settings: {
            ...settings,
            properties,
            required: settings.required?.filter((key) => key !== 'num_outputs')
        },
        ui: {
            ...model.ui,
            hiddenSettings,
            labelOverrides
        }
    };
};
const mapReplicateModel = (model) => {
    const mapped = {
        ...model,
        provider: 'replicate',
        rateLimitKey: resolveReplicateRateLimitKey(model)
    };
    return applyBatchGenerationOverrides(mapped);
};
const applyFalStudioOverrides = (model) => {
    if (isFalGptImageModel(model)) {
        const hiddenSettings = new Set(model.ui?.hiddenSettings ?? []);
        hiddenSettings.add('image_size');
        hiddenSettings.add('output_format');
        hiddenSettings.add('sync_mode');
        return {
            ...model,
            ui: {
                ...model.ui,
                hiddenSettings: Array.from(hiddenSettings)
            }
        };
    }
    if (!isFalSeedreamModel(model)) {
        return model;
    }
    const hiddenSettings = new Set(model.ui?.hiddenSettings ?? []);
    hiddenSettings.add('aspect_ratio');
    hiddenSettings.add('size');
    const labelOverrides = {
        ...(model.ui?.labelOverrides ?? {}),
        image_size: 'Canvas preset'
    };
    return {
        ...model,
        ui: {
            ...model.ui,
            hiddenSettings: Array.from(hiddenSettings),
            labelOverrides
        }
    };
};
const ELEVENLABS_AUDIO_MODELS = [
    {
        id: 'elevenlabs/scribe-v1',
        provider: 'elevenlabs',
        label: 'ElevenLabs Scribe',
        description: 'High-accuracy speech-to-text transcription built for podcasts, long-form conversations, and studio workflows.',
        kind: 'audio',
        model: 'scribe_v1',
        slug: 'scribe-v1',
        url: 'https://elevenlabs.io/docs/capabilities/speech-to-text',
        defaultSettings: {
            model_id: 'scribe_v1',
            diarize: false,
            tag_audio_events: false
        },
        pricing: {
            provider: {
                price: 0.22,
                currency: 'USD',
                unit: 'per hour of transcript audio',
                metric: 'audio_duration_seconds',
                priceText: '$0.22 per hour',
                description: 'ElevenLabs API speech-to-text list price for Scribe v1/v2.'
            },
            retail: {
                price: 0.242,
                currency: 'USD',
                unit: 'per hour of transcript audio',
                metric: 'audio_duration_seconds',
                priceText: '$0.24 per hour of audio',
                description: 'Athwab retail rate (≈1.1× markup on the current provider rate).',
                credits: 24,
                minimumCredits: 1
            },
            notes: 'ElevenLabs API pricing lists Scribe v1/v2 at 0.22 USD per hour before markup. Keyterm prompting and entity detection are priced as separate hourly add-ons and are not enabled in this direct Studio surface.'
        },
        settings: {
            required: ['file'],
            properties: {
                file: {
                    title: 'Audio or video file',
                    type: 'string',
                    description: 'Upload an audio or video file (MP3, WAV, M4A, MP4). Large podcasts are supported via direct uploads.',
                    format: 'uri',
                    order: 1
                },
                language_code: {
                    title: 'Language code',
                    type: 'string',
                    description: 'Optional ISO 639-1/3 code (for example: en, es, fr, ar). Leave blank to auto-detect.',
                    order: 2
                },
                diarize: {
                    title: 'Speaker diarization',
                    type: 'boolean',
                    description: 'Label different speakers in the transcript.',
                    order: 3
                },
                tag_audio_events: {
                    title: 'Tag audio events',
                    type: 'boolean',
                    description: 'Detect events such as applause, laughter, or music.',
                    order: 4
                }
            }
        },
        ui: {
            labelOverrides: {
                file: 'Upload file',
                language_code: 'Language (optional)',
                diarize: 'Identify speakers',
                tag_audio_events: 'Detect audio events'
            },
            hiddenSettings: ['model_id', 'language_code', 'diarize', 'tag_audio_events']
        },
        rateLimitKey: 'elevenlabs:transcription'
    },
    {
        id: 'elevenlabs/sound-effects-v1',
        provider: 'elevenlabs',
        label: 'ElevenLabs Sound Effects',
        description: 'Generate production-ready sound effects for films, trailers, games, and immersive clips using ElevenLabs text-to-sound.',
        kind: 'audio',
        model: 'sound_generation',
        slug: 'sound-effects-v1',
        url: 'https://elevenlabs.io/docs/api-reference/text-to-sound-effects/convert',
        defaultSettings: {
            output_format: 'pcm_24000',
            loop: false,
            prompt_influence: 0.6,
            duration_seconds: 6
        },
        pricing: {
            provider: {
                price: 0.12,
                currency: 'USD',
                unit: 'per generation',
                metric: 'generation_count',
                priceText: '$0.12 per generation',
                description: 'ElevenLabs API sound effects list price.'
            },
            retail: {
                price: 0.132,
                currency: 'USD',
                unit: 'per generation',
                metric: 'generation_count',
                priceText: '$0.13 per generation',
                description: 'Athwab retail rate (≈1.1× markup on the current provider rate).',
                credits: 13,
                minimumCredits: 13
            },
            notes: 'ElevenLabs API pricing lists Sound Effects at 0.12 USD per generation before markup. Duration steers the generated clip length but does not change this direct-provider estimate.'
        },
        settings: {
            required: ['prompt', 'duration_seconds'],
            properties: {
                prompt: {
                    title: 'Prompt',
                    type: 'string',
                    description: 'Describe the sound effect you want to create (for example: “Futuristic UI chime with airy reverb”).',
                    order: 0
                },
                output_format: {
                    title: 'Output format',
                    type: 'string',
                    description: 'Format of the generated audio. Set codec, sample rate, and bitrate. Higher bitrate/sample rate options may require an upgraded ElevenLabs plan.',
                    enum: ['mp3_44100_128', 'mp3_44100_192', 'pcm_24000', 'pcm_48000', 'ulaw_8000', 'opus_48000_128'],
                    order: 1
                },
                duration_seconds: {
                    title: 'Duration (seconds)',
                    type: 'number',
                    description: 'Length of the generated sound effect in seconds.',
                    default: 6,
                    minimum: 3,
                    maximum: 21,
                    multipleOf: 3,
                    order: 2
                },
                loop: {
                    title: 'Loop seamlessly',
                    type: 'boolean',
                    description: 'Enable to make the sound effect loop cleanly from end to start.',
                    default: false,
                    order: 3
                },
                prompt_influence: {
                    title: 'Prompt influence',
                    type: 'number',
                    description: 'Control how strongly the model should follow the prompt. Higher values follow the prompt more literally.',
                    default: 0.6,
                    minimum: 0,
                    maximum: 1,
                    multipleOf: 0.1,
                    order: 4
                },
                model_id: {
                    title: 'Model ID',
                    type: 'string',
                    description: 'Override the ElevenLabs sound generation model. Leave blank to use the latest default sound effects model.',
                    order: 5
                }
            }
        },
        ui: {
            labelOverrides: {
                duration_seconds: 'Duration (seconds)',
            },
            hiddenSettings: ['model_id', 'output_format', 'prompt_influence']
        },
        rateLimitKey: 'elevenlabs:sound-effects'
    }
];
const GOOGLE_MUSIC_MODELS = [
    {
        id: 'google/lyria-3-pro-preview',
        provider: 'google',
        label: 'Google Lyria 3 Pro',
        description: 'Generate full original songs, nasheeds, jingles, and background music with Google Lyria 3 Pro.',
        kind: 'audio',
        model: 'lyria-3-pro-preview',
        slug: 'lyria-3-pro-preview',
        url: 'https://ai.google.dev/gemini-api/docs/music-generation',
        defaultSettings: {
            durationSeconds: 60
        },
        pricing: {
            provider: {
                price: 0.08,
                currency: 'USD',
                unit: 'per song',
                metric: 'generation_count',
                priceText: '$0.08 per song',
                description: 'Google Gemini API Lyria 3 Pro Preview rate.'
            },
            retail: {
                price: 0.09,
                currency: 'USD',
                unit: 'per song',
                metric: 'generation_count',
                priceText: '$0.09 per song',
                description: 'Athwab retail rate (≈1.1× markup on the current Google list price).',
                credits: 9,
                minimumCredits: 9
            },
            notes: 'Lyria 3 Pro Preview pricing follows the official Google Gemini API pricing page: 0.08 USD per song provider rate, 0.09 USD per song Athwab retail after markup. Runtime target is optional and used for prompting, not per-second billing.'
        },
        settings: {
            required: ['durationSeconds'],
            properties: {
                durationSeconds: {
                    title: 'Target duration (seconds)',
                    type: 'integer',
                    description: 'Optional runtime target to steer the composition. Lyria still generates a full song and may vary slightly around the requested length.',
                    default: 60,
                    minimum: 30,
                    maximum: 180,
                    multipleOf: 1,
                    order: 1
                }
            }
        },
        ui: {
            labelOverrides: {
                durationSeconds: 'Target duration (seconds)'
            }
        },
        rateLimitKey: 'google:lyria-3-pro'
    }
];
const FAL_MODELS = listFalModels().map(applyFalStudioOverrides);
const ENABLED_FAL_ELEVENLABS_AUDIO_IDS = new Set([
    'fal-ai/elevenlabs/sound-effects-v2',
    'fal-ai/elevenlabs/tts-eleven-v3',
    'fal-ai/elevenlabs/speech-to-text/scribe-v2'
]);
const isAudioModelEnabled = (model) => {
    if (model.kind !== 'audio') {
        return true;
    }
    if (model.provider === 'google') {
        return true;
    }
    if (model.provider !== 'fal') {
        return false;
    }
    if (!model.id.startsWith('fal-ai/elevenlabs')) {
        return false;
    }
    return ENABLED_FAL_ELEVENLABS_AUDIO_IDS.has(model.id);
};
const isShadowedByFalModel = (replicateModel) => {
    const candidates = new Set();
    if (replicateModel.id) {
        candidates.add(replicateModel.id);
    }
    if (replicateModel.model) {
        candidates.add(replicateModel.model);
    }
    if (replicateModel.owner && replicateModel.model) {
        candidates.add(`${replicateModel.owner}/${replicateModel.model}`);
    }
    if (replicateModel.slug) {
        candidates.add(replicateModel.slug);
    }
    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }
        if (resolveFalCanonicalModelId(candidate)) {
            return true;
        }
    }
    return false;
};
const REPLICATE_MODELS = listOfficialReplicateModels()
    .map(mapReplicateModel)
    .filter((replicateModel) => !isShadowedByFalModel(replicateModel));
const STUDIO_MODELS = [...FAL_MODELS, ...ELEVENLABS_AUDIO_MODELS, ...GOOGLE_MUSIC_MODELS].filter(isAudioModelEnabled);
export const listStudioModels = () => STUDIO_MODELS;
export const listStudioModelsByKind = (kind) => STUDIO_MODELS.filter((model) => model.kind === kind);
export const getStudioModelById = (id) => getFalModelById(id) ?? STUDIO_MODELS.find((model) => model.id === id);
export const listReplicateModels = listOfficialReplicateModels;
export const listReplicateModelsByKind = listOfficialReplicateModelsByKind;
export const findReplicateModelById = (id) => REPLICATE_MODELS.find((model) => model.id === id);
//# sourceMappingURL=models.js.map
